import type { Currency } from './currency.js';
import type { InquiryCapabilities } from './capabilities.js';
import type { ChatSessionIntegrationResult } from './llm-chat.js';
import { MatchSource } from './matching.js';
import { ProfileDto } from './profile.js';

export const InquiryStatus = {
  Draft: 'draft',
  InReview: 'in-review',
  Published: 'published',
  Archived: 'archived',
  Evaluated: 'evaluated',
};

export const InquiryStatusValues = Object.values(InquiryStatus);

export type InquiryStatus = (typeof InquiryStatus)[keyof typeof InquiryStatus];

/** Inquiry statuses that cannot be edited by inquirer accounts (REST, LLM, FE). */
export const NON_EDITABLE_INQUIRY_STATUSES: InquiryStatus[] = [
  InquiryStatus.InReview,
  InquiryStatus.Evaluated,
  InquiryStatus.Archived,
];

export const ProjectType = {
  Development: 'development',
  Consulting: 'consulting',
  Implementation: 'implementation',
  Maintenance: 'maintenance',
  Training: 'training',
  Other: 'other',
};

export const ProjectTypeValues = Object.values(ProjectType);

export type ProjectType = (typeof ProjectType)[keyof typeof ProjectType];

export interface LookupItemDto {
  id: string;
  /** Display name from translation for current locale */
  name?: string;
  /** All translations (locale -> name) */
  translations?: { locale: string; name: string }[];
}

export const TeamRequirement = {
  None: 'none',
  IndividualExpert: 'individual-expert',
  ConsultingTeam: 'consulting-team',
  SolutionProvider: 'solution-provider',
};

export const TeamRequirementValues = Object.values(TeamRequirement);

export type TeamRequirement = (typeof TeamRequirement)[keyof typeof TeamRequirement];

export const ConfidentialityLevel = {
  Public: 'public',
  Internal: 'internal',
  Confidential: 'confidential',
  StrictlyConfidential: 'strictly-confidential',
};

export const ConfidentialityLevelValues = Object.values(ConfidentialityLevel);

export type ConfidentialityLevel =
  (typeof ConfidentialityLevel)[keyof typeof ConfidentialityLevel];

export const InquiryVersioningChoice = {
  UpdateCurrent: 'update-current',
  CreateNew: 'create-new',
};

export const InquiryVersioningChoiceValues = Object.values(InquiryVersioningChoice);

export type InquiryVersioningChoice =
  (typeof InquiryVersioningChoice)[keyof typeof InquiryVersioningChoice];

export interface InquiryKpiDto {
  id?: string;
  description: string;
  value?: number;
  unit?: string;
}

export interface InquiryAttachmentDto {
  id: string;
  inquiryId: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  uploadedAt: string;
  description?: string;
  ndaProtected?: boolean;
}

export interface InquiryDto {
  id: string;
  accountId: string;
  /** Public brand/company label for the inquirer account (display name or legal name). */
  accountDisplayName?: string;
  title: string;
  objectives: string;
  keyContact?: ProfileDto;
  categoryId: LookupItemDto['id'];
  category: LookupItemDto;
  industryIds: LookupItemDto['id'][];
  industries: LookupItemDto[];
  specification: string;
  deliverableIds: LookupItemDto['id'][];
  deliverables: LookupItemDto[];
  startDate?: string;
  endDate?: string;
  deadline: string;
  budgetMin?: number;
  budgetMax?: number;
  budgetCurrency: string;
  currency: Currency;
  timeCommitment?: string;
  expertiseIds: LookupItemDto['id'][];
  expertises: LookupItemDto[];
  teamRequirement: TeamRequirement;
  optionalReqs?: string;
  ndaRequired: boolean;
  /** Present for supplier views when NDA is required. */
  ndaAccepted?: boolean;
  ndaAcceptedAt?: string;
  internalNotes?: string;
  projectCode?: string;
  /** Visible to SUPERADMIN/SUPPORT and inquirer account members for own inquiries. */
  reviewSubmissionNote?: string;
  /** Set when brief was returned from review; same visibility as reviewSubmissionNote. */
  reviewRejectionReason?: string;
  kpis: InquiryKpiDto[];
  attachments: InquiryAttachmentDto[];
  status: InquiryStatus;
  currentVersionNumber?: number;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  /** Present on create when chatSessionId was sent in the request. */
  chatSessionIntegration?: ChatSessionIntegrationResult;
  /** Server-computed action flags for the current user. */
  capabilities?: InquiryCapabilities;
  /** Distinct supplier accounts with at least one offer (admin list). */
  offersCount?: number;
}

export interface RejectInquiryDto {
  rejectionReason: string;
}

export interface CreateInquiryDto {
  title: string;
  objectives: string;
  categoryId: LookupItemDto['id'];
  industryIds: LookupItemDto['id'][];
  specification: string;
  deliverableIds: LookupItemDto['id'][];
  startDate?: string;
  endDate?: string;
  deadline: string;
  budgetMin?: number;
  budgetMax?: number;
  budgetCurrency: string;
  currency: Currency;
  timeCommitment?: string;
  expertiseIds: LookupItemDto['id'][];
  teamRequirement: TeamRequirement;
  optionalReqs?: string;
  ndaRequired: boolean;
  internalNotes?: string;
  projectCode?: string;
  kpis?: InquiryKpiDto[];
  /** Optional LLM chat session to link and copy attachments from on create. */
  chatSessionId?: string;
  /** Optional project concept to link after inquiry is created (promotion flow). */
  projectConceptId?: string;
  /** Optional decision case to link after inquiry is created (approved decision flow). */
  decisionCaseId?: string;
}

export interface UpdateInquiryDto {
  versioningChoice?: InquiryVersioningChoice;
  title?: string;
  objectives?: string;
  categoryId?: LookupItemDto['id'];
  industryIds?: LookupItemDto['id'][];
  specification?: string;
  deliverableIds?: LookupItemDto['id'][];
  startDate?: string;
  endDate?: string;
  deadline?: string;
  budgetMin?: number;
  budgetMax?: number;
  budgetCurrency?: string;
  currency?: Currency;
  timeCommitment?: string;
  expertiseIds?: LookupItemDto['id'][];
  teamRequirement?: TeamRequirement;
  optionalReqs?: string;
  ndaRequired?: boolean;
  internalNotes?: string;
  projectCode?: string;
  kpis?: InquiryKpiDto[];
}

/** BE-only multipart field; use Express.Multer.File on the server. */
export interface UploadAttachmentDto {
  file: unknown;
}

export interface InquiryPublicDto {
  id: string;
  /** Public brand/company label for the inquirer account when the brief is published. */
  accountDisplayName?: string;
  title: string;
  objectives: string;
  categoryId: LookupItemDto['id'];
  category: LookupItemDto;
  industryIds: LookupItemDto['id'][];
  industries: LookupItemDto[];
  specification: string;
  deliverableIds: LookupItemDto['id'][];
  deliverables: LookupItemDto[];
  startDate?: string;
  endDate?: string;
  deadline: string;
  budgetMin?: number;
  budgetMax?: number;
  budgetCurrency: string;
  currency: Currency;
  timeCommitment?: string;
  expertiseIds: LookupItemDto['id'][];
  expertises: LookupItemDto[];
  teamRequirement: TeamRequirement;
  optionalReqs?: string;
  ndaRequired: boolean;
  ndaAccepted?: boolean;
  ndaAcceptedAt?: string;
  projectCode?: string;
  kpis: InquiryKpiDto[];
  attachments: InquiryAttachmentDto[];
  status: InquiryStatus;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  currentVersionNumber?: number;
  userOffer?: {
    id: string;
    status: import('./offer.js').OfferStatus;
    inquiryVersionNumber?: number;
  };
  /** Server-computed action flags for the current user. */
  capabilities?: InquiryCapabilities;
}

export interface MatchDto {
  id: string;
  inquiryId: string;
  accountId: string;
  accountName: string;
  score: number;
  source: MatchSource;
  createdAt: string;
  updatedAt: string;
}

export interface CreateMatchDto {
  accountId: string;
  score: number;
}

export interface UpdateMatchDto {
  score: number;
}

export interface InquiryNdaAcceptanceDto {
  id: string;
  inquiryId: string;
  accountId: string;
  acceptedByUserId: string;
  acceptedAt: string;
}

export function canRunMatching(inquiry: Pick<InquiryDto, 'status'>): boolean {
  return ! [InquiryStatus.Evaluated, InquiryStatus.Archived].includes(inquiry.status);
}
